import { useState } from 'react'
import { Text, View } from 'react-native'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Image } from '@/components/ui/Image'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { Skeleton } from '@/components/ui/Skeleton'
import { AlertTriangle, Clock, ShieldCheck } from '@/components/ui/icons'
import { TourRouteMap } from '@/components/tours/TourRouteMap'
import { TourTripTimeline } from '@/components/tours/TourTripTimeline'
import { useTourGuardian } from '@/lib/hooks/use-tours'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useSemanticColors } from '@/lib/theme/tokens'
import { FONTS, RADIUS, SPACING } from '@/lib/brand'
import { formatTourDeparture } from '@/lib/tours/format'
import { guardianScreenState } from '@/lib/tours/guardian-screen-state'
import { tourDetailTwoColumn } from '@/lib/tours/catalog-layout'
import { tourMapCanvasHeight } from '@/lib/maps/tour-map-layout'

export default function GuardianTripScreen() {
  const params = useLocalSearchParams<{ token?: string | string[] }>()
  const token = Array.isArray(params.token) ? (params.token[0] ?? '') : (params.token ?? '')
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop, isTablet, width } = useBreakpoint()
  const editorial = isDesktop || isTablet
  const mapHeight = tourMapCanvasHeight({
    editorial,
    twoColumn: tourDetailTwoColumn(editorial, width),
  })
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null)
  const trip = useTourGuardian(token)
  const screenState = guardianScreenState({
    isLoading: trip.isLoading,
    hasSnapshot: trip.data != null,
  })

  const header = (
    <View
      style={{
        minHeight: 72,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: SPACING.lg,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
      }}
    >
      <Image
        source={require('../../assets/images/logo-horizontal.png')}
        width={72}
        height={36}
        contentFit="contain"
        accessibilityLabel={t('common.app_name')}
      />
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          paddingHorizontal: SPACING.sm,
          paddingVertical: 6,
          borderRadius: RADIUS.full,
          backgroundColor: colors.surfaceSubtle,
        }}
      >
        <ShieldCheck size={15} color={colors.primary} />
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textSecondary }}>
          {t('tours.guardian.public_title')}
        </Text>
      </View>
    </View>
  )

  if (screenState === 'loading') {
    return (
      <ScreenContainer variant="content">
        {header}
        <View style={{ padding: SPACING.lg, gap: SPACING.md }}>
          <Skeleton width="72%" height={24} />
          <Skeleton width="100%" height={96} borderRadius={RADIUS.xl} staggerIndex={1} />
          <Skeleton width="100%" height={300} borderRadius={RADIUS.xl} staggerIndex={2} />
        </View>
      </ScreenContainer>
    )
  }

  if (screenState === 'invalid' || !trip.data) {
    return (
      <ScreenContainer variant="content">
        {header}
        <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: SPACING.lg }}>
          <EmptyState
            illustration={<ShieldCheck size={44} color={colors.textMuted} strokeWidth={1.5} />}
            title={t('tours.guardian.link_invalid')}
            description={t('tours.guardian.link_invalid_body')}
          />
        </View>
      </ScreenContainer>
    )
  }

  const data = trip.data
  const status = data.run?.status ?? 'scheduled'
  const currentPosition = data.run?.currentStopPosition ?? 0
  const emergencyStatus = data.run?.emergencyStatus ?? 'none'

  return (
    <ScreenContainer variant="content">
      {header}
      <View style={{ padding: SPACING.lg, paddingBottom: SPACING['2xl'], gap: SPACING.md }}>
        <View>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 25, color: colors.textPrimary }}>
            {data.experience.title}
          </Text>
          <Text
            style={{
              marginTop: 4,
              fontFamily: FONTS.body,
              fontSize: 14,
              color: colors.textSecondary,
            }}
          >
            {t('tours.trip.route', {
              origin: data.experience.originLabel,
              destination: data.experience.destinationLabel,
            })}
          </Text>
        </View>

        <Card>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
            <View
              style={{
                width: 44,
                height: 44,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 22,
                backgroundColor: `${colors.primary}12`,
              }}
            >
              <ShieldCheck size={22} color={colors.primary} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 17, color: colors.textPrimary }}>
                {t(`tours.trip.status.${status}`)}
              </Text>
              <View style={{ marginTop: 3, flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                <Clock size={14} color={colors.textMuted} />
                <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}>
                  {formatTourDeparture(data.departure.startsAt, i18n.language)}
                </Text>
              </View>
            </View>
          </View>
          <Text
            style={{
              marginTop: SPACING.sm,
              fontFamily: FONTS.body,
              fontSize: 12,
              color: colors.textMuted,
            }}
          >
            {t('tours.guardian.shared_with', { name: data.guardianName })}
          </Text>
        </Card>

        {emergencyStatus !== 'none' && emergencyStatus !== 'resolved' ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
              padding: SPACING.md,
              borderRadius: RADIUS.lg,
              backgroundColor: `${colors.error}12`,
            }}
          >
            <AlertTriangle size={22} color={colors.textError} />
            <Text
              style={{ flex: 1, fontFamily: FONTS.bodyBold, fontSize: 14, color: colors.textError }}
            >
              {t(`tours.trip.emergency.${emergencyStatus}`)}
            </Text>
          </View>
        ) : null}

        <TourRouteMap
          stops={data.stops}
          latestLocation={data.latestLocation}
          routeGeojson={data.experience.routeGeojson}
          canvasHeight={mapHeight}
          selectedStopId={selectedStopId}
          onStopPress={setSelectedStopId}
          testID="tours.guardian-map"
        />

        <Card>
          <Text
            style={{
              marginBottom: SPACING.md,
              fontFamily: FONTS.bodyBold,
              fontSize: 17,
              color: colors.textPrimary,
            }}
          >
            {t('tours.trip.itinerary')}
          </Text>
          <TourTripTimeline
            stops={data.stops}
            currentPosition={currentPosition}
            selectedStopId={selectedStopId}
            onStopPress={setSelectedStopId}
          />
        </Card>

        <Text
          style={{
            paddingHorizontal: SPACING.sm,
            fontFamily: FONTS.body,
            fontSize: 12,
            lineHeight: 18,
            textAlign: 'center',
            color: colors.textMuted,
          }}
        >
          {t('tours.guardian.privacy_note')}
        </Text>
      </View>
    </ScreenContainer>
  )
}
